import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import TrebbleCard from '../common/TrebbleCard';
import TrebbleButton from '../common/TrebbleButton';
import TrebbleSegmented from '../common/TrebbleSegmented';
import TrebbleSelect from '../common/TrebbleSelect';
import TrebbleSwitch from '../common/TrebbleSwitch';
import TrebbleDivider from '../common/Divider';
import { TrebbleText } from '../common/TrebbleTypography';
import ModalHeader from '../common/ModalHeader';
import TrebbleInfoTooltip from '../common/TrebbleInfoTooltip';
import { FieldContainer } from '../clipCreator/Components/TemplateConfigurator/CaptionConfig/FieldContainer';

export const ExportContent = ({
    hasVideo = false,
    isSelection = false, // New prop: true for selection-based, false for project-based
    isSequencerAlreadyInstantiated = false, // New prop: true if sequencer already instantiated, false for capsule-based
    isVideoReady = true, // New prop: true if video is ready for download
    isVideoBeingRendered = false, // New prop: true if video is currently being rendered
    onExport,
    onCancel,
    i18nModule,
    useEditorTheme = false
}) => {
    const [exportType, setExportType] = useState(hasVideo ? 'video' : 'audio');
    const [format, setFormat] = useState('wav');
    const [includeMedia, setIncludeMedia] = useState(true);
    const [includeSpeakers, setIncludeSpeakers] = useState(true);
    const [createTrackForEachFile, setCreateTrackForEachFile] = useState(true);
    const [frameRate, setFrameRate] = useState(25);
    const [skipAudioProcessing, setSkipAudioProcessing] = useState(false);

    // Helper function for i18n
    const t = (key) => window.getI18n(i18nModule, key);

    // Update format when export type changes
    useEffect(() => {
        switch (exportType) {
            case 'audio':
                setFormat('wav');
                break;
            case 'video':
                setFormat('mp4');
                break;
            case 'transcript':
                setFormat('txt');
                break;
            case 'subtitles':
                setFormat('srt');
                break;
            case 'timeline':
                setFormat('samplitude');
                break;
            default:
                break;
        }
    }, [exportType]);

    // Get format options based on export type
    const getFormatOptions = () => {
        switch (exportType) {
            case 'audio':
                return [
                    { 
                        value: 'mp3', 
                        label: t('MP3_FORMAT'),
                        description: t('MP3_FORMAT_DESCRIPTION') 
                    },
                    { 
                        value: 'wav', 
                        label: t('WAV_FORMAT'),
                        description: t('WAV_FORMAT_DESCRIPTION') 
                    }
                ];
            case 'video':
                return [
                    { value: 'mp4', label: t('MP4_FORMAT') }
                ];
            case 'transcript':
                return [
                    { 
                        value: 'txt', 
                        label: t('TXT_FORMAT'),
                        description: t('TXT_FORMAT_DESCRIPTION') 
                    },
                    { 
                        value: 'docx', 
                        label: t('DOCX_FORMAT'),
                        description: t('DOCX_FORMAT_DESCRIPTION') 
                    }
                ];
            case 'subtitles':
                return [
                    { 
                        value: 'srt', 
                        label: t('SRT_FORMAT'),
                        description: t('SRT_FORMAT_DESCRIPTION') 
                    },
                    { 
                        value: 'vtt', 
                        label: t('VTT_FORMAT'),
                        description: t('VTT_FORMAT_DESCRIPTION') 
                    }
                ];
            case 'timeline':
                return [
                    { value: 'fcpxml', label: t('FCPXML_FORMAT') },
                    { value: 'premiere', label: t('PREMIERE_XML_FORMAT') },
                    { value: 'sesx', label: t('SESX_FORMAT') },
                    { value: 'samplitude', label: t('SAMPLITUDE_EDL_FORMAT') },
                    { value: 'cmx3600', label: t('CMX3600_EDL_FORMAT') },
                ];
            default:
                return [];
        }
    };


    // Get frame rate options
    const getFrameRateOptions = () => [
        { value: 23.976, label: '23.976 fps' },
        { value: 24, label: '24 fps' },
        { value: 25, label: '25 fps' },
        { value: 29.97, label: '29.97 fps' },
        { value: 30, label: '30 fps' },
        { value: 60, label: '60 fps' }
    ];

    // Export type options for TrebbleSegmented
    const exportTypeOptions = [
        ...(hasVideo ? [{
            value: 'video',
            label: t('VIDEO')
        }] : []),
        {
            value: 'audio',
            label: t('AUDIO')
        },
        {
            value: 'transcript',
            label: t('TRANSCRIPT')
        },
        {
            value: 'subtitles',
            label: t('SUBTITLES')
        },
        {
            value: 'timeline',
            label: t('TIMELINE')
        }
    ];

    // Export type descriptions for display below segmented control
    const getExportTypeDescription = (type) => {
        const suffix = isSelection ? '_SELECTION' : '_PROJECT';
        switch (type) {
            case 'audio':
                return t(`AUDIO_DESCRIPTION${suffix}`);
            case 'video':
                return t(`VIDEO_DESCRIPTION${suffix}`);
            case 'transcript':
                return t(`TRANSCRIPT_DESCRIPTION${suffix}`);
            case 'subtitles':
                return t(`SUBTITLES_DESCRIPTION${suffix}`);
            case 'timeline':
                return t(`TIMELINE_DESCRIPTION${suffix}`);
            default:
                return '';
        }
    };

    const handleExport = () => {
        const exportOptions = {
            type: exportType,
            format: format,
            includeMedia: includeMedia,
            includeSpeakers: includeSpeakers,
            createTrackForEachFile: createTrackForEachFile,
            frameRate: frameRate,
            skipAudioProcessing: exportType === 'audio' ? skipAudioProcessing : false
        };

        onExport(exportOptions);
    };

    // Check if export should be disabled
    const isExportDisabled = () => {
        return exportType === 'video' && !isSequencerAlreadyInstantiated && (!isVideoReady || isVideoBeingRendered);
    };

    const renderTypeSpecificOptions = () => {
        switch (exportType) {
            case 'audio':
                return (
                    <div>
                        <FieldContainer
                            label={t('SKIP_AUDIO_PROCESSING')}
                            description={t('SKIP_AUDIO_PROCESSING_DESCRIPTION')}
                            direction="row"
                            labelClassName="font-bold"
                        >
                            <TrebbleSwitch
                                checked={skipAudioProcessing}
                                onChange={setSkipAudioProcessing}
                                useEditorTheme={useEditorTheme}
                                size="small"
                            />
                        </FieldContainer>
                    </div>
                );

            case 'video':
                return (
                    <div>
                        {!isSequencerAlreadyInstantiated && (!isVideoReady || isVideoBeingRendered) && (
                            <TrebbleText 
                                size="small" 
                                type="danger"
                                className="italic"
                                disableDefaultColors={useEditorTheme}
                            >
                                {isVideoBeingRendered ? t('VIDEO_BEING_RENDERED_WARNING') : t('VIDEO_NOT_READY_WARNING')}
                            </TrebbleText>
                        )}
                        {isSequencerAlreadyInstantiated && (
                            <TrebbleText 
                                size="small" 
                                type="secondary"
                                className="italic"
                                disableDefaultColors={useEditorTheme}
                            >
                                {t('VIDEO_GENERATION_MESSAGE')}
                            </TrebbleText>
                        )}
                    </div>
                );

            case 'subtitles':
                return (
                    <div>
                        <FieldContainer 
                            label={t('INCLUDE_SPEAKERS')} 
                            direction="row"
                            labelClassName="font-bold"
                        >
                            <TrebbleSwitch
                                checked={includeSpeakers}
                                onChange={setIncludeSpeakers}
                                useEditorTheme={useEditorTheme}
                                size="small"
                            />
                        </FieldContainer>
                    </div>
                );

            case 'timeline':
                return (
                    <div>
                         <FieldContainer 
                            label={t('FRAME_RATE')} 
                            direction="row"
                            labelClassName="font-bold"
                        >
                            <TrebbleSelect
                                value={frameRate}
                                onChange={setFrameRate}
                                options={getFrameRateOptions()}
                                useEditorTheme={useEditorTheme}
                                popupMatchSelectWidth={false}
                                placement="bottomRight"
                            />
                        </FieldContainer>
                        <FieldContainer 
                            label={t('INCLUDE_MEDIA_FILES')} 
                            description={t('INCLUDE_MEDIA_FILES_DESCRIPTION')}
                            direction="row"
                            labelClassName="font-bold"
                        >
                            <TrebbleSwitch
                                checked={includeMedia}
                                onChange={setIncludeMedia}
                                useEditorTheme={useEditorTheme}
                                size="small"
                            />
                        </FieldContainer>
                        
                        <FieldContainer 
                            label={t('CREATE_TRACK_FOR_EACH_FILE')} 
                            direction="row"
                            labelClassName="font-bold"
                        >
                            <TrebbleSwitch
                                checked={createTrackForEachFile}
                                onChange={setCreateTrackForEachFile}
                                useEditorTheme={useEditorTheme}
                                size="small"
                            />
                        </FieldContainer>

                       
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <TrebbleCard 
            useEditorTheme={useEditorTheme} 
            className="modal_content"
        >
            <div className="flex flex-col">
                <div className="mb-4 shrink-0">
                    <ModalHeader
                        title={isSelection ? t('EXPORT_SELECTION') : t('EXPORT_PROJECT')}
                        useEditorTheme={useEditorTheme}
                        marginBottom={0}
                    />
                </div>

                {/* Export Type Selection */}
                <div className="shrink-0">
                    <FieldContainer 
                        label={t('CHOOSE_EXPORT_TYPE')} 
                        labelClassName="font-bold"
                    >
                        <TrebbleSegmented
                            value={exportType}
                            onChange={setExportType}
                            options={exportTypeOptions}
                            useEditorTheme={useEditorTheme}
                        />
                    </FieldContainer>
                </div>

            <TrebbleDivider useEditorTheme={useEditorTheme} />

                {/* Format Selection - Always show format selector */}
                <div>
                    <FieldContainer 
                        label={t('CHOOSE_FORMAT')} 
                        direction="row"
                        labelClassName="font-bold"
                    >
                        <TrebbleSelect
                            value={format}
                            onChange={setFormat}
                            useEditorTheme={useEditorTheme}
                            popupMatchSelectWidth={false}
                            optionLabelProp="label"
                            placement="bottomRight"
                        >
                            {getFormatOptions().map(option => (
                                <TrebbleSelect.Option 
                                    key={option.value} 
                                    value={option.value}
                                    label={option.label}
                                >
                                    <div className="flex flex-col py-1">
                                        <div className="font-medium text-sm">
                                            {option.label}
                                        </div>
                                        {option.description && (
                                            <div className="text-xs font-medium text-gray-500 mt-1">
                                                {option.description}
                                            </div>
                                        )}
                                    </div>
                                </TrebbleSelect.Option>
                            ))}
                        </TrebbleSelect>
                    </FieldContainer>
                </div>

                {/* Type-specific Options - Fixed height container */}
                <div className="mb-8">
                    {renderTypeSpecificOptions()}
                </div>

                {/* Action Buttons - Fixed at bottom */}
                <div className={`flex gap-3 justify-end pt-4 mt-4 shrink-0`}>
                    <TrebbleButton 
                        onClick={onCancel}
                        useEditorTheme={useEditorTheme}
                    >
                        {t('CANCEL')}
                    </TrebbleButton>
                    
                    <TrebbleButton 
                        type="primary"
                        onClick={handleExport}
                        useEditorTheme={useEditorTheme}
                        disabled={isExportDisabled()}
                    >
                        {t('EXPORT')}
                    </TrebbleButton>
                </div>
            </div>
        </TrebbleCard>
    );
};

ExportContent.propTypes = {
    hasVideo: PropTypes.bool,
    isSelection: PropTypes.bool,
    isSequencerAlreadyInstantiated: PropTypes.bool,
    isVideoReady: PropTypes.bool,
    isVideoBeingRendered: PropTypes.bool,
    onExport: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    i18nModule: PropTypes.object.isRequired,
    useEditorTheme: PropTypes.bool
};